import {Action} from "redux-actions";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";
import Asset from "../../../model/asset/Asset";
import {
	ASSET_CATEGORIES_FETCHED,
	ASSET_CATEGORY_CREATED,
	ASSET_CATEGORY_SORT_ORDER_UPDATED,
	ASSET_CATEGORY_UPDATED
} from "./actionType";
import AssetCategory from "../../../model/asset/AssetCategory";
import DictionaryItem from "../../../model/dictionary/DictionaryItem";

/**
 * Asset categories fetched successful action.
 * @param {Array<Asset>} assetCategories all asset categories from server.
 * @returns {Action<ASSET_CATEGORIES_FETCHED>}
 */
function assetCategoriesFetched(assetCategories: Array<AssetCategory>): Action<ASSET_CATEGORIES_FETCHED> {
	return {type: ASSET_CATEGORIES_FETCHED, payload: {assetCategories}};
}

/**
 * Asset Category created successful action.
 * @param {AssetCategory} assetCategory
 * @returns {Action<ASSET_CATEGORY_CREATED>}
 */
function assetCategoryCreated(assetCategory: AssetCategory): Action<ASSET_CATEGORY_CREATED> {
	return {type: ASSET_CATEGORY_CREATED, payload: {assetCategory}};
}

/**
 * Asset Category updated successful action.
 * @param {AssetCategory} assetCategory
 * @returns {Action<ASSET_CATEGORY_UPDATED>}
 */
function assetCategoryUpdated(assetCategory: AssetCategory): Action<ASSET_CATEGORY_UPDATED> {
	return {type: ASSET_CATEGORY_UPDATED, payload: {assetCategory}};
}

/**
 * Update asset categories sort order successful action.
 * @param {Array<AssetCategory>} assetCategories
 * @returns {Action<ASSET_CATEGORY_SORT_ORDER_UPDATED>}
 */
function assetCategoriesSortOrderUpdated(assetCategories: Array<AssetCategory>): Action<ASSET_CATEGORY_SORT_ORDER_UPDATED> {
	return {type: ASSET_CATEGORY_SORT_ORDER_UPDATED, payload: {assetCategories}};
}

export function fetchAssetCategories(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetCategoryApi.fetchAssetCategories().then(assetCategories => {
			dispatch(assetCategoriesFetched(assetCategories));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Create asset category.
 * @param {AssetCategory} assetCategory
 * @param {OnSuccessData<AssetCategory>} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createAssetCategory(assetCategory: AssetCategory, onSuccess: OnSuccessData<AssetCategory>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetCategoryApi.createAssetCategory(assetCategory).then(ac => {
			dispatch(assetCategoryCreated(ac));
			onSuccess(ac);
		}).catch(onFailure);
	};
}

/**
 * Update asset category.
 * @param {string} acId
 * @param {AssetCategory} assetCategory
 * @param {OnSuccessData<DictionaryItem>} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateAssetCategory(
	acId: string,
	assetCategory: AssetCategory,
	onSuccess: OnSuccessData<DictionaryItem>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetCategoryApi.updateAssetCategory(acId, assetCategory).then(() => {
			dispatch(assetCategoryUpdated(assetCategory));
			onSuccess(assetCategory);
		}).catch(onFailure);
	};
}

/**
 * Update asset categories sort order.
 * @param {AssetCategory[]} assetCategories
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateAssetCategoriesSortOrder(assetCategories: AssetCategory[],
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetCategoryApi.updateSortOrder(assetCategories).then(() => {
			dispatch(assetCategoriesSortOrderUpdated(assetCategories));
			dispatch(onSuccess);
		}).catch(onFailure);
	};
}